import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import { useStripe } from '../hooks/useStripe';
import { stripeProducts } from '../stripe-config';

// ============================================================================
// MYSTARA LANDING PAGE - NOSEDIVE EDITION
// Premium, editorial, aspirational. Every pixel intentional.
// ============================================================================

// Subtle star/sparkle icon - custom, delicate
const StarIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0L13.5 8.5L22 10L13.5 11.5L12 20L10.5 11.5L2 10L10.5 8.5L12 0Z" />
  </svg>
);

// Minimal check icon
const CheckIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ============================================================================
// VIDEO CARD - For "How Mystara Works" section
// ============================================================================
function VideoCard({ 
  videoSrc, 
  step, 
  title, 
  description, 
  delay = 0 
}: { 
  videoSrc: string; 
  step: number; 
  title: string; 
  description: string; 
  delay?: number;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = () => {
    setIsHovered(true);
    videoRef.current?.play();
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      viewport={{ once: true }}
      className="group cursor-pointer"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Video Container */}
      <div className="relative aspect-[4/5] rounded-3xl overflow-hidden mb-6 shadow-2xl shadow-pink-200/50 group-hover:shadow-pink-300/60 transition-all duration-700">
        <video
          ref={videoRef}
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        >
          <source src={videoSrc} type="video/mp4" />
        </video>
        
        {/* Elegant gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500" />
        
        {/* Step number - elegant positioning */}
        <div className="absolute top-6 left-6">
          <span className="text-white/90 text-sm font-light tracking-widest uppercase">
            Step {step}
          </span>
        </div>
      </div>
      
      {/* Text content */}
      <h3 
        className="text-2xl font-medium mb-3 transition-colors duration-300"
        style={{ 
          color: '#be185d',
          fontFamily: "'Playfair Display', Georgia, serif"
        }}
      >
        {title}
      </h3>
      <p className="text-pink-800/60 leading-relaxed">
        {description}
      </p>
    </motion.div>
  );
}

// ============================================================================
// FEATURE CARD - Editorial style with image placeholder
// ============================================================================
function FeatureCard({
  title,
  description,
  imagePlaceholder,
  delay = 0,
  large = false
}: {
  title: string;
  description: string;
  imagePlaceholder: string;
  delay?: number;
  large?: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      viewport={{ once: true }}
      className={`group cursor-pointer ${large ? 'md:col-span-2' : ''}`}
    >
      {/* Image placeholder - you'll fill these */}
      <div className={`relative ${large ? 'aspect-[2/1]' : 'aspect-[4/3]'} rounded-3xl overflow-hidden mb-5 bg-gradient-to-br from-pink-100 via-rose-50 to-pink-50`}>
        {/* Placeholder indicator - remove when you add images */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-pink-300 text-sm tracking-wide uppercase">
            {imagePlaceholder}
          </span>
        </div>
        
        {/* Hover effect overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-pink-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>
      
      <h3 
        className="text-xl font-medium mb-2 group-hover:text-pink-700 transition-colors duration-300"
        style={{ 
          color: '#be185d',
          fontFamily: "'Playfair Display', Georgia, serif"
        }}
      >
        {title}
      </h3>
      <p className="text-pink-800/50 text-sm leading-relaxed">
        {description}
      </p>
    </motion.div>
  );
}

// ============================================================================
// FEATURE CARD WITH IMAGE - Editorial style with actual images
// ============================================================================
function FeatureCardWithImage({
  title,
  description,
  imageSrc,
  delay = 0,
  large = false
}: {
  title: string;
  description: string;
  imageSrc: string;
  delay?: number;
  large?: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      viewport={{ once: true }}
      className={`group cursor-pointer ${large ? 'md:col-span-2' : ''}`}
    >
      {/* Image container */}
      <div className={`relative ${large ? 'aspect-[2/1]' : 'aspect-[4/3]'} rounded-3xl overflow-hidden mb-5 shadow-lg shadow-pink-100/50 group-hover:shadow-xl group-hover:shadow-pink-200/50 transition-all duration-500`}>
        <img 
          src={imageSrc}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        
        {/* Hover effect overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-pink-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>
      
      <h3 
        className="text-xl font-medium mb-2 group-hover:text-pink-700 transition-colors duration-300"
        style={{ 
          color: '#be185d',
          fontFamily: "'Playfair Display', Georgia, serif"
        }}
      >
        {title}
      </h3>
      <p className="text-pink-800/50 text-sm leading-relaxed">
        {description}
      </p>
    </motion.div>
  );
}

// ============================================================================
// TESTIMONIAL CARD - Magazine editorial style
// ============================================================================
function TestimonialCard({
  quote,
  name,
  role,
  rating,
  delay = 0
}: {
  quote: string;
  name: string;
  role: string;
  rating: number;
  delay?: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      viewport={{ once: true }}
      className="relative"
    >
      {/* Glass card */}
      <div className="relative p-8 rounded-3xl bg-white/40 backdrop-blur-xl border border-white/60 hover:bg-white/50 transition-all duration-500">
        {/* Rating stars */}
        <div className="flex gap-1 mb-6">
          {[...Array(rating)].map((_, i) => (
            <StarIcon key={i} className="w-4 h-4 text-pink-400" />
          ))}
        </div>
        
        {/* Quote */}
        <blockquote 
          className="text-lg leading-relaxed mb-8"
          style={{ 
            color: '#831843',
            fontFamily: "'Playfair Display', Georgia, serif",
            fontStyle: 'italic'
          }}
        >
          "{quote}"
        </blockquote>
        
        {/* Attribution */}
        <div className="flex items-center gap-4">
          {/* Avatar placeholder */}
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-200 to-rose-100" />
          <div>
            <p className="font-medium text-pink-900">{name}</p>
            <p className="text-sm text-pink-600/60">{role}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ============================================================================
// MAIN LANDING PAGE
// ============================================================================
export function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { createCheckoutSession, loading: stripeLoading } = useStripe();
  
  const handleUpgradeClick = async () => {
    if (!user) return;
    const proProduct = stripeProducts.find(p => p.name === 'Mystara AI PRO');
    if (!proProduct) return;
    await createCheckoutSession({
      priceId: proProduct.priceId,
      mode: proProduct.mode,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 via-white to-pink-50">
      
      {/* ================================================================== */}
      {/* FLOATING NAVIGATION */}
      {/* ================================================================== */}
      <div className="fixed top-6 right-6 z-50 flex items-center gap-3">
        <button
          onClick={() => navigate(user ? '/' : '/auth/sign-in')}
          className="px-6 py-3 bg-white/70 backdrop-blur-xl text-pink-900 font-medium rounded-full hover:bg-white/90 transition-all duration-300 text-sm border border-white/60 shadow-lg shadow-pink-100/50"
        >
          {user ? 'Dashboard' : 'Sign In'}
        </button>
        <button
          onClick={() => navigate(user ? '/' : '/auth/sign-up')}
          className="px-6 py-3 bg-pink-600 text-white font-medium rounded-full hover:bg-pink-700 transition-all duration-300 text-sm shadow-lg shadow-pink-300/50"
        >
          {user ? 'Continue' : 'Get Started'}
        </button>
      </div>

      {/* ================================================================== */}
      {/* HERO SECTION */}
      {/* ================================================================== */}
      <HeroSection navigate={navigate} user={user} />

      {/* ================================================================== */}
      {/* SOCIAL PROOF BAR - Subtle, premium */}
      {/* ================================================================== */}
      <section className="py-12 px-6 border-y border-pink-100/50">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16 text-pink-400/60 text-sm tracking-wide">
            <span>10,000+ transformations</span>
            <span className="hidden md:block">·</span>
            <span>4.9 rating</span>
            <span className="hidden md:block">·</span>
            <span>Featured in Vogue Wellness</span>
            <span className="hidden md:block">·</span>
            <span>As seen in MindBodyGreen</span>
          </div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* HOW MYSTARA WORKS - Video Cards */}
      {/* ================================================================== */}
      <section className="py-32 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section header */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <p className="text-pink-400 text-sm tracking-widest uppercase mb-4">
              The Method
            </p>
            <h2 
              className="text-4xl md:text-5xl lg:text-6xl font-light mb-6"
              style={{ 
                color: '#be185d',
                fontFamily: "'Playfair Display', Georgia, serif"
              }}
            >
              How Mystara Works
            </h2>
            <p className="text-pink-800/50 text-lg max-w-xl mx-auto">
              Manifestation isn't about wishing. It's about becoming.
            </p>
          </motion.div>

          {/* Video cards grid */}
          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            <VideoCard
              videoSrc="/rain.mp4"
              step={1}
              title="See Yourself Clearly"
              description="Through reflection, chat, and insight — understand who you are and who you're becoming."
              delay={0}
            />
            <VideoCard
              videoSrc="/matcha.mp4"
              step={2}
              title="Shift Your Identity"
              description="Affirmations, aligned actions, and daily rituals rewire your identity from the inside out."
              delay={0.15}
            />
            <VideoCard
              videoSrc="/car.mp4"
              step={3}
              title="Watch Reality Respond"
              description="Track evidence, celebrate synchronicities, and see your manifestations materialize."
              delay={0.3}
            />
          </div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* EDITORIAL FEATURE - Full width image moment */}
      {/* ================================================================== */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
            viewport={{ once: true }}
            className="relative aspect-[21/9] rounded-[2.5rem] overflow-hidden"
          >
            {/* Editorial image */}
            <img 
              src="/car.png" 
              alt="Elegant woman driving a vintage convertible"
              className="absolute inset-0 w-full h-full object-cover"
            />
            
            {/* Subtle gradient overlay for text readability */}
            <div className="absolute inset-0 bg-gradient-to-r from-pink-50/80 via-pink-50/40 to-transparent" />
            
            {/* Overlay text */}
            <div className="absolute inset-0 flex items-center justify-start p-12 md:p-20">
              <div className="max-w-lg">
                <p className="text-pink-600/80 text-sm tracking-widest uppercase mb-4">
                  Your Daily Ritual
                </p>
                <h3 
                  className="text-3xl md:text-4xl lg:text-5xl font-light text-pink-900/90 mb-4"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  Transform your mornings.
                  <br />
                  <span className="italic">Transform your life.</span>
                </h3>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* YOUR SANCTUARY - Editorial Feature Grid */}
      {/* ================================================================== */}
      <section className="py-32 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section header */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <p className="text-pink-400 text-sm tracking-widest uppercase mb-4">
              The Experience
            </p>
            <h2 
              className="text-4xl md:text-5xl lg:text-6xl font-light mb-6"
              style={{ 
                color: '#be185d',
                fontFamily: "'Playfair Display', Georgia, serif"
              }}
            >
              Your Daily Sanctuary
            </h2>
            <p className="text-pink-800/50 text-lg max-w-xl mx-auto">
              Eight rituals designed to shift your identity, one day at a time.
            </p>
          </motion.div>

          {/* Bento-style editorial grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Large feature - Chat */}
            <FeatureCardWithImage
              title="Chat with Mystara"
              description="Your AI guide for clarity, breakthroughs, and becoming who you need to be."
              imageSrc="/chat.png"
              delay={0}
              large={true}
            />
            
            {/* Oracle Cards */}
            <FeatureCardWithImage
              title="Oracle Cards"
              description="Pull cards for guidance and insight into your journey."
              imageSrc="/oracle.png"
              delay={0.1}
            />
            
            {/* Daily Alignment */}
            <FeatureCardWithImage
              title="Daily Alignment"
              description="Morning intentions and evening reflections."
              imageSrc="/align.png"
              delay={0.15}
            />
            
            {/* Affirmations */}
            <FeatureCardWithImage
              title="Affirmations"
              description="Personalized truths to rewire your identity."
              imageSrc="/affirm.png"
              delay={0.2}
            />
            
            {/* Vision Board - Large */}
            <FeatureCardWithImage
              title="Vision Board"
              description="Visualize your future self with images that anchor your manifestations into reality."
              imageSrc="/vision.png"
              delay={0.25}
              large={true}
            />
            
            {/* Evidence Bank */}
            <FeatureCardWithImage
              title="Evidence Bank"
              description="Collect proof that your transformation is working."
              imageSrc="/evidence.png"
              delay={0.3}
            />
            
            {/* Gratitude Journal */}
            <FeatureCardWithImage
              title="Gratitude Journal"
              description="Daily practice to shift your focus and raise your vibration."
              imageSrc="/gratitude.png"
              delay={0.35}
            />
            
            {/* Aligned Actions */}
            <FeatureCardWithImage
              title="Aligned Actions"
              description="Small steps that align your daily actions with your future self."
              imageSrc="/aligned.png"
              delay={0.4}
            />
          </div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* TESTIMONIALS - Magazine Style */}
      {/* ================================================================== */}
      <section className="py-32 px-6 bg-gradient-to-b from-pink-50/50 to-white">
        <div className="max-w-6xl mx-auto">
          {/* Section header */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <p className="text-pink-400 text-sm tracking-widest uppercase mb-4">
              Transformations
            </p>
            <h2 
              className="text-4xl md:text-5xl lg:text-6xl font-light mb-6"
              style={{ 
                color: '#be185d',
                fontFamily: "'Playfair Display', Georgia, serif"
              }}
            >
              Stories of Becoming
            </h2>
          </motion.div>

          {/* Testimonial cards */}
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            <TestimonialCard
              quote="Mystara helped me see myself differently. Within weeks, opportunities I'd been chasing for years started flowing to me."
              name="Sarah K."
              role="Entrepreneur"
              rating={5}
              delay={0}
            />
            <TestimonialCard
              quote="The daily rituals completely changed my morning. I feel more aligned and intentional than I ever have."
              name="Michelle R."
              role="Creative Director"
              rating={5}
              delay={0.15}
            />
            <TestimonialCard
              quote="I was skeptical, but tracking evidence of my transformation made it feel real. Now I'm living proof it works."
              name="Jessica T."
              role="Life Coach"
              rating={5}
              delay={0.3}
            />
          </div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* PRICING - Elegant, Nosedive-inspired */}
      {/* ================================================================== */}
      <section className="py-32 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Section header */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <p className="text-pink-400 text-sm tracking-widest uppercase mb-4">
              Membership
            </p>
            <h2 
              className="text-4xl md:text-5xl lg:text-6xl font-light mb-6"
              style={{ 
                color: '#be185d',
                fontFamily: "'Playfair Display', Georgia, serif"
              }}
            >
              Choose Your Path
            </h2>
          </motion.div>

          {/* Pricing cards */}
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            
            {/* Magic Plan - Free */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0 }}
              viewport={{ once: true }}
              className="relative p-8 rounded-3xl bg-white/40 backdrop-blur-xl border border-white/60 hover:bg-white/50 transition-all duration-500"
            >
              <div className="mb-8">
                <h3 
                  className="text-2xl font-medium mb-2"
                  style={{ 
                    color: '#be185d',
                    fontFamily: "'Playfair Display', Georgia, serif"
                  }}
                >
                  Magic
                </h3>
                <p className="text-pink-600/50 text-sm">Begin your journey</p>
              </div>
              
              <div className="mb-8">
                <span className="text-4xl font-light text-pink-900">$0</span>
                <span className="text-pink-600/50 ml-2">/month</span>
              </div>
              
              <ul className="space-y-4 mb-8">
                {['5 AI conversations daily', 'Daily affirmations', 'Gratitude journal', 'Aligned actions', 'Basic insights'].map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-pink-800/70">
                    <CheckIcon className="w-5 h-5 text-pink-400 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              
              <button
                onClick={() => navigate(user ? '/' : '/auth/sign-up')}
                className="w-full py-4 rounded-full border border-pink-200 text-pink-700 font-medium hover:bg-pink-50 transition-all duration-300"
              >
                {user ? 'Current Plan' : 'Start Free'}
              </button>
            </motion.div>

            {/* Trial Plan */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              viewport={{ once: true }}
              className="relative p-8 rounded-3xl bg-white/40 backdrop-blur-xl border border-white/60 hover:bg-white/50 transition-all duration-500"
            >
              <div className="mb-8">
                <h3 
                  className="text-2xl font-medium mb-2"
                  style={{ 
                    color: '#be185d',
                    fontFamily: "'Playfair Display', Georgia, serif"
                  }}
                >
                  Trial
                </h3>
                <p className="text-pink-600/50 text-sm">Experience everything</p>
              </div>
              
              <div className="mb-8">
                <span className="text-4xl font-light text-pink-900">$0</span>
                <span className="text-pink-600/50 ml-2">/7 days</span>
              </div>
              
              <ul className="space-y-4 mb-8">
                {['Full Cosmic access', '35 AI conversations', 'All premium features', 'Oracle cards', 'No commitment'].map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-pink-800/70">
                    <CheckIcon className="w-5 h-5 text-pink-400 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              
              <button
                onClick={() => navigate(user ? '/' : '/auth/sign-up')}
                className="w-full py-4 rounded-full border border-pink-200 text-pink-700 font-medium hover:bg-pink-50 transition-all duration-300"
              >
                Start Trial
              </button>
            </motion.div>

            {/* Cosmic Plan - Premium */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
              className="relative p-8 rounded-3xl bg-gradient-to-br from-pink-600 via-rose-500 to-pink-500 text-white shadow-2xl shadow-pink-300/50"
            >
              {/* Subtle badge */}
              <div className="absolute -top-3 left-8">
                <span className="px-4 py-1.5 bg-white text-pink-600 text-xs font-medium tracking-wide rounded-full shadow-lg">
                  Most Popular
                </span>
              </div>
              
              <div className="mb-8 pt-2">
                <h3 
                  className="text-2xl font-medium mb-2 text-white"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  Cosmic
                </h3>
                <p className="text-white/70 text-sm">Unlimited transformation</p>
              </div>
              
              <div className="mb-8">
                <span className="text-4xl font-light">$9.99</span>
                <span className="text-white/70 ml-2">/month</span>
              </div>
              
              <ul className="space-y-4 mb-8">
                {['Unlimited conversations', 'All manifestation tools', 'Vision board creation', 'Oracle cards', 'Guided visualizations', 'Priority support'].map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/90">
                    <CheckIcon className="w-5 h-5 text-white/80 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              
              <button
                onClick={(e) => {
                  if (user) {
                    e.preventDefault();
                    handleUpgradeClick();
                  } else {
                    navigate('/auth/sign-up');
                  }
                }}
                disabled={stripeLoading}
                className="w-full py-4 rounded-full bg-white text-pink-600 font-medium hover:bg-white/90 transition-all duration-300 shadow-lg disabled:opacity-50"
              >
                {stripeLoading ? 'Processing...' : 'Upgrade to Cosmic'}
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* FINAL CTA - Cinematic */}
      {/* ================================================================== */}
      <section className="py-32 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
            viewport={{ once: true }}
            className="relative p-16 md:p-24 rounded-[3rem] bg-gradient-to-br from-pink-100 via-rose-50 to-pink-50 overflow-hidden"
          >
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-pink-200/40 to-transparent rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-rose-200/40 to-transparent rounded-full blur-3xl" />
            
            <div className="relative text-center">
              <motion.p 
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-pink-400 text-sm tracking-widest uppercase mb-6"
              >
                Your transformation awaits
              </motion.p>
              
              <h2 
                className="text-4xl md:text-5xl lg:text-6xl font-light mb-8"
                style={{ 
                  color: '#be185d',
                  fontFamily: "'Playfair Display', Georgia, serif"
                }}
              >
                Ready to watch your
                <br />
                <span className="italic">world bloom?</span>
              </h2>
              
              <p className="text-pink-800/50 text-lg mb-12 max-w-lg mx-auto">
                Join thousands who have transformed their reality through the power of identity-first manifestation.
              </p>
              
              <button
                onClick={() => navigate(user ? '/' : '/auth/sign-up')}
                className="px-12 py-5 bg-pink-600 text-white text-lg font-medium rounded-full hover:bg-pink-700 transition-all duration-300 shadow-2xl shadow-pink-300/50 hover:shadow-pink-400/50 hover:scale-105"
              >
                Begin Your Journey
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* FOOTER - Minimal, elegant */}
      {/* ================================================================== */}
      <footer className="py-16 px-6 border-t border-pink-100/50">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <StarIcon className="w-5 h-5 text-pink-400" />
              <span 
                className="text-xl font-medium"
                style={{ 
                  color: '#be185d',
                  fontFamily: "'Playfair Display', Georgia, serif"
                }}
              >
                Mystara
              </span>
            </div>

            {/* Tagline */}
            <p 
              className="text-pink-600/50 italic"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Watch your world bloom.
            </p>

            {/* Links */}
            <div className="flex items-center gap-8 text-sm">
              <Link to="/privacy" className="text-pink-600/50 hover:text-pink-600 transition-colors">
                Privacy
              </Link>
              <Link to="/terms" className="text-pink-600/50 hover:text-pink-600 transition-colors">
                Terms
              </Link>
              <a href="mailto:hello@mystara.app" className="text-pink-600/50 hover:text-pink-600 transition-colors">
                Contact
              </a>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-pink-100/30 text-center">
            <p className="text-pink-400/40 text-xs tracking-wide">
              © {new Date().getFullYear()} Mystara. Transform your identity, transform your reality.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ============================================================================
// HERO SECTION - Cinematic, video-driven
// ============================================================================
function HeroSection({ navigate, user }: { navigate: (path: string) => void; user: any }) {
  const [showTitle, setShowTitle] = useState(false);
  const [showTagline, setShowTagline] = useState(false);
  const [showCTA, setShowCTA] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const titleTimer = setTimeout(() => setShowTitle(true), 500);
    const taglineTimer = setTimeout(() => setShowTagline(true), 1200);
    const ctaTimer = setTimeout(() => setShowCTA(true), 1800);

    return () => {
      clearTimeout(titleTimer);
      clearTimeout(taglineTimer);
      clearTimeout(ctaTimer);
    };
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Video Background */}
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        preload="auto"
        className="absolute inset-0 w-full h-full object-cover"
      >
        <source src="/mystara.mp4" type="video/mp4" />
      </video>
      
      {/* Subtle gradient overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-transparent" />

      {/* Content */}
      <div className="relative z-10 pl-8 sm:pl-12 lg:pl-20 w-full max-w-4xl">
        {/* MYSTARA Title */}
        <h1
          className={`font-bold text-white mb-4 transition-all duration-1000 ease-out ${
            showTitle
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-8'
          }`}
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(72px, 14vw, 140px)',
            letterSpacing: '-0.02em',
            lineHeight: '0.9',
            textShadow: '0 4px 60px rgba(0,0,0,0.15)'
          }}
        >
          MYSTARA
        </h1>

        {/* Tagline */}
        <div
          className={`inline-block transition-all duration-1000 ease-out ${
            showTagline
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-8'
          }`}
        >
          <p
            className="text-xl sm:text-2xl md:text-3xl px-6 py-3 rounded-full"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontStyle: 'italic',
              color: '#be185d',
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              backdropFilter: 'blur(12px)'
            }}
          >
            Watch your world bloom.
          </p>
        </div>

        {/* CTA Buttons */}
        <div
          className={`flex flex-col sm:flex-row gap-4 mt-8 transition-all duration-1000 ease-out ${
            showCTA
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-8'
          }`}
        >
          <button
            onClick={() => navigate(user ? '/' : '/auth/sign-up')}
            className="px-8 py-4 bg-pink-600 text-white text-lg font-medium rounded-full shadow-2xl shadow-pink-500/30 hover:bg-pink-700 hover:shadow-pink-500/40 hover:scale-105 transition-all duration-300"
          >
            Begin Your Journey
          </button>
          <button
            onClick={() => navigate(user ? '/' : '/auth/sign-in')}
            className="px-8 py-4 bg-white/70 backdrop-blur-xl text-pink-900 text-lg font-medium rounded-full hover:bg-white/80 transition-all duration-300"
          >
            Sign In
          </button>
        </div>
      </div>
    </section>
  );
}
